import { generateNonExtractableKeyMaterial, generateWrappedKeyMaterial, inspectKeyMaterial, isStoredKeyMaterial, signWithKeyMaterial, } from "./key-custody.js";
import { deriveDeviceIdentifiers } from "./did.js";
import { BrowserIdentityLifecycleLock, } from "./identity-lock.js";
import { IndexedDbIdentityStore, } from "../storage/identity-store.js";
export class IdentityUnavailableError extends Error {
    constructor() {
        super("Device identity is temporarily unavailable");
        this.name = "IdentityUnavailableError";
    }
}
export class IdentityRevokedError extends Error {
    constructor() {
        super("Device identity is revoked");
        this.name = "IdentityRevokedError";
    }
}
export class WebCryptoIdentity {
    store;
    cryptoProvider;
    lifecycleLock;
    constructor(store = new IndexedDbIdentityStore(), cryptoProvider = globalThis.crypto, lifecycleLock = new BrowserIdentityLifecycleLock()) {
        this.store = store;
        this.cryptoProvider = cryptoProvider;
        this.lifecycleLock = lifecycleLock;
    }
    async generate() {
        return await this.lifecycleLock.runExclusive(async () => await this.#generateLocked());
    }
    async sign(keyHandle, payload) {
        return await this.lifecycleLock.runExclusive(async () => {
            const keyMaterial = await this.#readVerifiedKeyMaterial();
            if (keyMaterial.keyId !== keyHandle.id)
                throw new IdentityUnavailableError();
            try {
                return await signWithKeyMaterial(this.cryptoProvider, keyMaterial, payload);
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async publicIdentity() {
        return await this.lifecycleLock.runExclusive(async () => {
            const keyMaterial = await this.#readKeyMaterial();
            return isStoredKeyMaterial(keyMaterial)
                ? publicIdentityFromKeyMaterial(keyMaterial)
                : undefined;
        });
    }
    async loadDelegation() {
        return await this.lifecycleLock.runExclusive(async () => {
            try {
                return await this.store.readDelegation();
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async storeDelegation(delegation) {
        await this.lifecycleLock.runExclusive(async () => {
            await this.#assertActiveIdentity();
            try {
                await this.store.writeDelegation({ ...delegation, scopes: [...delegation.scopes] });
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async loadConnectionMetadata() {
        return await this.lifecycleLock.runExclusive(async () => {
            try {
                return await this.store.readConnectionMetadata();
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async storeConnectionMetadata(metadata) {
        await this.lifecycleLock.runExclusive(async () => {
            await this.#assertActiveIdentity();
            try {
                await this.store.writeConnectionMetadata({
                    ...metadata,
                    directAddresses: [...metadata.directAddresses],
                });
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async clearDelegation() {
        await this.lifecycleLock.runExclusive(async () => {
            try {
                await this.store.clearDelegation();
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async clear() {
        await this.lifecycleLock.runExclusive(async () => {
            try {
                await this.store.markRevoked();
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async recoverAfterRevocation() {
        return await this.lifecycleLock.runExclusive(async () => {
            const lifecycle = await this.#readLifecycle();
            if (lifecycle?.state !== "revoked") {
                throw new IdentityUnavailableError();
            }
            let generated;
            try {
                generated = await generateNonExtractableKeyMaterial(this.cryptoProvider);
            }
            catch {
                throw new IdentityUnavailableError();
            }
            try {
                if (await this.store.replaceRevoked(lifecycle.generation, generated.keyMaterial)) {
                    return identityFromKeyMaterial(generated.keyMaterial);
                }
                throw new IdentityUnavailableError();
            }
            catch (error) {
                if (!isCloneUnsupported(error))
                    throw new IdentityUnavailableError();
            }
            try {
                const wrapped = await generateWrappedKeyMaterial(this.cryptoProvider);
                if (await this.store.replaceRevoked(lifecycle.generation, wrapped.keyMaterial)) {
                    return identityFromKeyMaterial(wrapped.keyMaterial);
                }
            }
            catch {
                throw new IdentityUnavailableError();
            }
            throw new IdentityUnavailableError();
        });
    }
    async diagnostics() {
        return await this.lifecycleLock.runExclusive(async () => {
            try {
                const keyMaterial = await this.store.readKeyMaterial();
                const delegation = await this.store.readDelegation();
                if (!isStoredKeyMaterial(keyMaterial)) {
                    return { hasDelegation: delegation !== undefined };
                }
                return {
                    custodyMode: keyMaterial.custodyMode,
                    hasDelegation: delegation !== undefined,
                    keyId: keyMaterial.keyId,
                };
            }
            catch {
                throw new IdentityUnavailableError();
            }
        });
    }
    async #generateLocked() {
        const lifecycle = await this.#readLifecycle();
        if (lifecycle?.state === "revoked")
            throw new IdentityRevokedError();
        const existing = await this.#readKeyMaterial();
        if (existing !== undefined) {
            let inspection;
            try {
                inspection = await inspectKeyMaterial(this.cryptoProvider, existing);
            }
            catch {
                throw new IdentityUnavailableError();
            }
            if (inspection === "valid" && isStoredKeyMaterial(existing)) {
                return identityFromKeyMaterial(existing);
            }
            try {
                await this.store.purgeCorrupt();
            }
            catch {
                throw new IdentityUnavailableError();
            }
        }
        let generated;
        try {
            generated = await generateNonExtractableKeyMaterial(this.cryptoProvider);
        }
        catch {
            throw new IdentityUnavailableError();
        }
        try {
            await this.store.writeKeyMaterial(generated.keyMaterial);
            return identityFromKeyMaterial(generated.keyMaterial);
        }
        catch (error) {
            if (!isCloneUnsupported(error))
                throw new IdentityUnavailableError();
        }
        try {
            const wrapped = await generateWrappedKeyMaterial(this.cryptoProvider);
            await this.store.writeKeyMaterial(wrapped.keyMaterial);
            return identityFromKeyMaterial(wrapped.keyMaterial);
        }
        catch {
            throw new IdentityUnavailableError();
        }
    }
    async #readVerifiedKeyMaterial() {
        const lifecycle = await this.#readLifecycle();
        if (lifecycle?.state === "revoked")
            throw new IdentityRevokedError();
        const keyMaterial = await this.#readKeyMaterial();
        if (keyMaterial === undefined)
            throw new IdentityUnavailableError();
        try {
            if (await inspectKeyMaterial(this.cryptoProvider, keyMaterial) === "valid" && isStoredKeyMaterial(keyMaterial)) {
                return keyMaterial;
            }
        }
        catch {
            throw new IdentityUnavailableError();
        }
        throw new IdentityUnavailableError();
    }
    async #assertActiveIdentity() {
        const lifecycle = await this.#readLifecycle();
        if (lifecycle?.state === "revoked")
            throw new IdentityRevokedError();
        if (!isStoredKeyMaterial(await this.#readKeyMaterial()))
            throw new IdentityUnavailableError();
    }
    async #readKeyMaterial() {
        try {
            return await this.store.readKeyMaterial();
        }
        catch {
            throw new IdentityUnavailableError();
        }
    }
    async #readLifecycle() {
        try {
            return await this.store.readLifecycle();
        }
        catch {
            throw new IdentityUnavailableError();
        }
    }
}
function identityFromKeyMaterial(keyMaterial) {
    return {
        ...publicIdentityFromKeyMaterial(keyMaterial),
        keyHandle: { id: keyMaterial.keyId },
    };
}
function publicIdentityFromKeyMaterial(keyMaterial) {
    return {
        ...deriveDeviceIdentifiers(keyMaterial.publicKey),
        custodyMode: keyMaterial.custodyMode,
    };
}
function isCloneUnsupported(error) {
    return error instanceof DOMException && error.name === "DataCloneError";
}
//# sourceMappingURL=webcrypto-identity.js.map